import { Image } from "@chakra-ui/image";
import { Flex } from "@chakra-ui/layout";
import { Tag } from "@chakra-ui/tag";
import { PessoaTipo } from "core/features/pessoas/typings";
import {
  UnidadePessoaPaginateResponse,
  TipoUnidadePessoaEnum,
} from "core/features/unidades/typings/unidade";
import { useMemo, useCallback } from "react";
import { BsBank, BsPeople, BsKey, BsCalendar3 } from "react-icons/bs";
import { MdPerson } from "react-icons/md";
import moment from "moment";
import { Switch } from "@chakra-ui/switch";
import { showErrorAxios } from "infra/helpers/alerta";
import useTogglePessoasStatus from "core/features/unidades/useTogglePessoasStatus";
import { useLoading } from "components/GlobalLoading";

function useTabelaPessoaColumns({
  unidadeId,
  onToogled,
}: {
  unidadeId: string;
  onToogled: () => void;
}) {
  const { isLoading, mutate: handleToggleStatus } =
    useTogglePessoasStatus(unidadeId);
  const _handleToggleStatus = useCallback(
    (id: string) => {
      handleToggleStatus(
        { id },
        {
          onError: (err) => {
            showErrorAxios(err, "Não foi possível alterar o status.");
          },
          onSuccess: () => {
            onToogled();
          },
        }
      );
    },
    [handleToggleStatus, onToogled]
  );

  useLoading(isLoading);

  return useMemo(() => {
    return [
      {
        title: "",
        dataIndex: "imagemUrl",
        width: 50,
        render: (value: string) =>
          value ? (
            <Image
              src={value}
              rounded="full"
              w="35px"
              h="35px"
              objectFit="cover"
              maxW="unset"
            />
          ) : (
            <Flex
              w="35px"
              h="35px"
              justifyContent="center"
              alignItems="center"
              rounded="full"
              bg="gray.100"
            >
              <MdPerson fontSize="18px" />
            </Flex>
          ),
      },
      {
        title: "Nome",
        dataIndex: "nome",
      },
      {
        title: "",
        render: (item: UnidadePessoaPaginateResponse) => {
          return (
            <Flex gridGap="8px">
              {item.tipoUnidadePessoaId ===
              TipoUnidadePessoaEnum.Proprietario ? (
                <Tag colorScheme="orange" size="sm">
                  <BsKey style={{ marginRight: 8 }} />
                  Proprietário
                </Tag>
              ) : null}

              {item.tipoUnidadePessoaId === TipoUnidadePessoaEnum.Locatario ? (
                <Tag variant="outline" colorScheme="orange" size="sm">
                  Inquilino
                </Tag>
              ) : null}

              {item.tipoVinculoPessoa ? (
                <Tag variant="outline" colorScheme="blue" size="sm">
                  <BsPeople style={{ marginRight: 8 }} />
                  {item.tipoVinculoPessoa}
                </Tag>
              ) : null}

              {item.pessoaTipo === PessoaTipo.Juridica ? (
                <Tag colorScheme="purple" size="sm">
                  <BsBank style={{ marginRight: 8 }} />
                  Pessoa Jurídica
                </Tag>
              ) : null}

              {item.dataFimAcesso ? (
                <Tag variant="outline" colorScheme="whatsapp" size="sm">
                  <BsCalendar3 style={{ marginRight: 8 }} />
                  Acesso permitido até{" "}
                  {moment(item.dataFimAcesso).format("DD/MM/YYYY")}
                </Tag>
              ) : null}
            </Flex>
          );
        },
      },
      {
        title: "Status",
        render: (original: UnidadePessoaPaginateResponse) => (
          <Switch
            isDisabled={isLoading}
            onChange={() => {
              _handleToggleStatus(original.id);
            }}
            isChecked={!!original.ativo}
          />
        ),
      },
    ];
  }, [_handleToggleStatus, isLoading]);
}

export default useTabelaPessoaColumns;
